import { useCallback, useEffect, useRef, useState } from 'react';
import { isMobileDevice } from '../utils/platform';

/**
 * Shared redirect logic for the Done page.
 * Redirect mode: auto-redirects after 3s unless the wallet handles it on mobile
 * (same-device). On desktop (cross-device), always redirect — the wallet
 * redirect on the phone does not affect the desktop browser.
 * Webhook mode (embedded): completes at once, also on mobile, because the
 * wallet has no redirect to take over and only this step finalises the flow.
 * `done` turns true once it succeeded: the result is on its way to the
 * website's backend.
 */
export function useDoneRedirect(
  onComplete: () => Promise<void>,
  continueInApp: boolean,
  deliveryMode: 'redirect' | 'webhook',
  delayMs = 3000,
) {
  const isMobile = isMobileDevice();
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState(false);
  const [done, setDone] = useState(false);
  const attempted = useRef(false);

  const complete = useCallback(async () => {
    setCompleting(true);
    setError(null);
    try {
      await onComplete();
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCompleting(false);
    }
  }, [onComplete]);

  useEffect(() => {
    // Only a redirect hands the finish to the wallet app; webhook mode has
    // nothing to hand over, so it always completes here.
    if (deliveryMode === 'redirect' && continueInApp && isMobile) return;
    if (attempted.current) return;
    attempted.current = true;

    // Webhook mode (embedded): complete at once; the backend calls the webhook.
    if (deliveryMode === 'webhook') {
      void complete();
      return;
    }

    const timer = setTimeout(() => void complete(), delayMs);
    return () => clearTimeout(timer);
  }, [complete, continueInApp, deliveryMode, isMobile, delayMs]);

  return { error, completing, done, handleManualComplete: complete };
}
